import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import { useEntitlements } from '@/hooks/useEntitlements'
import { setLaunchEntitlements, setBackendFlagOverrides } from '@/lib/launchGating'

/**
 * RR-5 bridge: launch gating (`isFeatureEnabled`/`isUrlEnabled`) is made of pure
 * module functions so the Sidebar + route guard can call them without hooks.
 * This invisible component lives near the top of the tree and keeps that
 * module's snapshot in sync with React state:
 *   - the user's tier + platform-admin (from useEntitlements), and
 *   - the backend per-workspace feature-flag overrides (/config/flags/resolved).
 * Render it once inside the auth + query providers; it renders nothing.
 */
export function LaunchEntitlementsSync() {
  const ent = useEntitlements()
  const isAuthed = useAuthStore((s) => !!s.user)

  useEffect(() => {
    setLaunchEntitlements({ tier: ent.tier, isPlatformAdmin: ent.isPlatformAdmin })
  }, [ent.tier, ent.isPlatformAdmin])

  const { data } = useQuery({
    queryKey: ['resolved-feature-flags'],
    queryFn: () =>
      apiFetch<{ flags: Record<string, boolean> }>('/api/marketing/config/flags/resolved'),
    enabled: isAuthed,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (data?.flags) setBackendFlagOverrides(data.flags)
  }, [data])

  return null
}
